import { useCustom } from "@refinedev/core";
import {
  Loader2,
  TrendingUp,
  CreditCard,
  Users,
  BookOpen,
  Target,
  MessageSquare,
  Clock,
  Calendar,
  CheckCircle2,
  AlertCircle,
  Crown,
  Heart,
  Eye,
  UserPlus,
  Share2,
  Bell,
  Flag,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";

interface UserAnalyticsData {
  financial: {
    ltv: number;
    totalOrders: number;
    confirmedPayments: number;
    averageOrderValue: number;
    currency: string;
    paymentsByProvider: Record<string, number>;
    paymentsByStatus: Record<string, number>;
  };
  subscription: {
    currentStatus: string | null;
    currentPlan: string | null;
    daysRemaining: number | null;
    totalSubscriptions: number;
    subscriptionHistory: Array<{
      id: number;
      status: string;
      startDate: string | null;
      endDate: string | null;
      planCode: string | null;
    }>;
    isFamilyOwner: boolean;
    familyMembersCount: number;
  };
  courses: {
    started: number;
    completed: number;
    inProgress: number;
    averageProgress: number;
    coursesProgress: Array<{
      courseId: number;
      courseTitle: string;
      totalItems: number;
      completedItems: number;
      progress: number;
      completedAt: string | null;
      lastActivity: string;
    }>;
    homeworkStats: {
      notStarted: number;
      submitted: number;
      reviewed: number;
    };
  };
  access: {
    totalCourses: number;
    totalChannels: number;
    totalEvents: number;
    bySourceType: Record<string, number>;
  };
  social: {
    postsPublished: number;
    totalViews: number;
    totalLikes: number;
    totalComments: number;
    commentsWritten: number;
    likesGiven: number;
    followersCount: number;
    followingCount: number;
  };
  planner: {
    goals: {
      total: number;
      completed: number;
      active: number;
      overdue: number;
    };
    intentions: {
      total: number;
      completed: number;
      published: number;
    };
    reminders: {
      total: number;
      completed: number;
      active: number;
      totalCompletions: number;
    };
  };
  general: {
    registeredAt: string;
    lastUpdatedAt: string;
    isActive: boolean;
    languagePreference: string;
    theme: string;
  };
}

interface UserOverviewAnalyticsProps {
  userId: number;
}

const COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

const formatCurrency = (value: number, currency: string) => {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export function UserOverviewAnalytics({ userId }: UserOverviewAnalyticsProps) {
  const analyticsQuery = useCustom<UserAnalyticsData>({
    url: `/users/${userId}/analytics`,
    method: "get",
  });

  const isLoading = analyticsQuery.query?.isLoading ?? false;
  const isError = analyticsQuery.query?.isError ?? false;
  const data = analyticsQuery.query?.data?.data;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
        <p className="text-muted-foreground">Не удалось загрузить аналитику</p>
      </div>
    );
  }

  const analytics = data as UserAnalyticsData;

  // Prepare data for charts
  const courseStatusData = [
    { name: "Завершено", value: analytics.courses.completed, color: COLORS[0] },
    { name: "В процессе", value: analytics.courses.inProgress, color: COLORS[1] },
    { name: "Начато", value: Math.max(0, analytics.courses.started - analytics.courses.completed - analytics.courses.inProgress), color: COLORS[2] },
  ].filter((d) => d.value > 0);

  const goalStatusData = [
    { name: "Выполнено", value: analytics.planner.goals.completed, color: COLORS[0] },
    { name: "Активные", value: analytics.planner.goals.active, color: COLORS[1] },
    { name: "Просрочено", value: analytics.planner.goals.overdue, color: COLORS[3] },
  ].filter((d) => d.value > 0);

  const accessSourceData = Object.entries(analytics.access.bySourceType).map(([name, value], i) => ({
    name: translateSourceType(name),
    value,
    fill: COLORS[i % COLORS.length],
  }));

  const paymentStatusData = Object.entries(analytics.financial.paymentsByStatus).map(([name, value], i) => ({
    name: translatePaymentStatus(name),
    value,
    fill: COLORS[i % COLORS.length],
  }));

  return (
    <div className="space-y-6">
      {/* Key Metrics Row */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* LTV Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">LTV</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(analytics.financial.ltv, analytics.financial.currency)}
            </div>
            <p className="text-xs text-muted-foreground">
              {analytics.financial.confirmedPayments} платежей
            </p>
          </CardContent>
        </Card>

        {/* Subscription Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Подписка</CardTitle>
            <Crown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <SubscriptionStatusBadge status={analytics.subscription.currentStatus} />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {analytics.subscription.currentPlan ?? "Нет плана"}
              {analytics.subscription.daysRemaining !== null && (
                <span className="ml-1">
                  ({analytics.subscription.daysRemaining} дн.)
                </span>
              )}
            </p>
          </CardContent>
        </Card>

        {/* Courses Progress Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Курсы</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics.courses.completed}/{analytics.courses.started}
            </div>
            <p className="text-xs text-muted-foreground">
              Средний прогресс: {analytics.courses.averageProgress}%
            </p>
          </CardContent>
        </Card>

        {/* Social Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Соцсеть</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.social.followersCount}</div>
            <p className="text-xs text-muted-foreground">
              подписчиков, {analytics.social.postsPublished} постов
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Financial Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Платежи
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center">
                <p className="text-2xl font-bold">{analytics.financial.totalOrders}</p>
                <p className="text-xs text-muted-foreground">Заказов</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">
                  {formatCurrency(analytics.financial.averageOrderValue, analytics.financial.currency)}
                </p>
                <p className="text-xs text-muted-foreground">Ср. чек</p>
              </div>
            </div>
            {paymentStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={150}>
                <BarChart data={paymentStatusData} layout="vertical">
                  <XAxis type="number" hide />
                  <YAxis dataKey="name" type="category" width={80} fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-muted-foreground text-sm py-8">Нет данных</p>
            )}
          </CardContent>
        </Card>

        {/* Course Progress Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Прогресс курсов
            </CardTitle>
          </CardHeader>
          <CardContent>
            {courseStatusData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={150}>
                  <PieChart>
                    <Pie
                      data={courseStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={60}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {courseStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend iconSize={10} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="text-center mt-2">
                  <p className="text-2xl font-bold">{analytics.courses.averageProgress}%</p>
                  <p className="text-xs text-muted-foreground">Средний прогресс</p>
                </div>
              </>
            ) : (
              <p className="text-center text-muted-foreground text-sm py-8">
                Нет начатых курсов
              </p>
            )}
          </CardContent>
        </Card>

        {/* Goals Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="h-4 w-4" />
              Цели
            </CardTitle>
          </CardHeader>
          <CardContent>
            {goalStatusData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={150}>
                  <PieChart>
                    <Pie
                      data={goalStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={60}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {goalStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend iconSize={10} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="text-center mt-2">
                  <p className="text-sm">
                    <span className="font-semibold">{analytics.planner.goals.completed}</span>
                    <span className="text-muted-foreground"> из {analytics.planner.goals.total}</span>
                  </p>
                </div>
              </>
            ) : (
              <p className="text-center text-muted-foreground text-sm py-8">
                Нет целей
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Cards Row */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Access Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Доступы
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Курсы</span>
              </div>
              <Badge variant="secondary">{analytics.access.totalCourses}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Каналы</span>
              </div>
              <Badge variant="secondary">{analytics.access.totalChannels}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Мероприятия</span>
              </div>
              <Badge variant="secondary">{analytics.access.totalEvents}</Badge>
            </div>
            {accessSourceData.length > 0 && (
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground mb-2">По источнику:</p>
                <div className="flex flex-wrap gap-1">
                  {accessSourceData.map((item) => (
                    <Badge key={item.name} variant="outline" className="text-xs">
                      {item.name}: {item.value}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Social Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Share2 className="h-4 w-4" />
              Социальная активность
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-2 rounded-lg bg-muted/50">
                <UserPlus className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
                <p className="text-lg font-semibold">{analytics.social.followersCount}</p>
                <p className="text-xs text-muted-foreground">Подписчики</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-muted/50">
                <Users className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
                <p className="text-lg font-semibold">{analytics.social.followingCount}</p>
                <p className="text-xs text-muted-foreground">Подписки</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <Flag className="h-3 w-3" /> Постов
                </span>
                <span className="font-medium">{analytics.social.postsPublished}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <Eye className="h-3 w-3" /> Просмотров
                </span>
                <span className="font-medium">{analytics.social.totalViews}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <Heart className="h-3 w-3" /> Лайков (получено)
                </span>
                <span className="font-medium">{analytics.social.totalLikes}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <MessageSquare className="h-3 w-3" /> Комментариев
                </span>
                <span className="font-medium">{analytics.social.commentsWritten}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Planner Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Планировщик
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Goals */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm flex items-center gap-1">
                  <Target className="h-3 w-3" /> Цели
                </span>
                <span className="text-xs text-muted-foreground">
                  {analytics.planner.goals.completed}/{analytics.planner.goals.total}
                </span>
              </div>
              <Progress
                value={
                  analytics.planner.goals.total > 0
                    ? (analytics.planner.goals.completed / analytics.planner.goals.total) * 100
                    : 0
                }
                className="h-2"
              />
              {analytics.planner.goals.overdue > 0 && (
                <p className="text-xs text-destructive mt-1">
                  {analytics.planner.goals.overdue} просрочено
                </p>
              )}
            </div>

            {/* Intentions */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm flex items-center gap-1">
                  <Flag className="h-3 w-3" /> Намерения
                </span>
                <span className="text-xs text-muted-foreground">
                  {analytics.planner.intentions.completed}/{analytics.planner.intentions.total}
                </span>
              </div>
              <Progress
                value={
                  analytics.planner.intentions.total > 0
                    ? (analytics.planner.intentions.completed / analytics.planner.intentions.total) * 100
                    : 0
                }
                className="h-2"
              />
            </div>

            {/* Reminders */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm flex items-center gap-1">
                  <Bell className="h-3 w-3" /> Напоминания
                </span>
                <span className="text-xs text-muted-foreground">
                  {analytics.planner.reminders.active} активных
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Всего выполнений: {analytics.planner.reminders.totalCompletions}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Course Progress Details */}
      {analytics.courses.coursesProgress.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Прогресс по курсам</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.courses.coursesProgress.slice(0, 5).map((course) => (
                <div key={course.courseId} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium truncate flex-1 mr-2">
                      {course.courseTitle}
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      {course.completedAt ? (
                        <Badge variant="default" className="bg-green-500">
                          Завершён
                        </Badge>
                      ) : (
                        <Badge variant="secondary">{course.progress}%</Badge>
                      )}
                    </div>
                  </div>
                  <Progress value={course.progress} className="h-2" />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>
                      {course.completedItems}/{course.totalItems} уроков
                    </span>
                    <span>Активность: {formatDate(course.lastActivity)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* General Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Общая информация</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div>
              <p className="text-xs text-muted-foreground">Регистрация</p>
              <p className="text-sm font-medium">{formatDate(analytics.general.registeredAt)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Последнее обновление</p>
              <p className="text-sm font-medium">{formatDate(analytics.general.lastUpdatedAt)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Язык</p>
              <p className="text-sm font-medium">{analytics.general.languagePreference.toUpperCase()}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Тема</p>
              <p className="text-sm font-medium capitalize">{analytics.general.theme}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SubscriptionStatusBadge({ status }: { status: string | null }) {
  if (!status) {
    return <Badge variant="outline">Нет подписки</Badge>;
  }

  switch (status.toUpperCase()) {
    case "ACTIVE":
      return <Badge className="bg-green-500">Активна</Badge>;
    case "EXPIRED":
      return <Badge variant="secondary">Истекла</Badge>;
    case "CANCELLED":
      return <Badge variant="destructive">Отменена</Badge>;
    case "PENDING":
      return <Badge variant="outline">Ожидание</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function translateSourceType(sourceType: string): string {
  const translations: Record<string, string> = {
    SUBSCRIPTION: "Подписка",
    MANUAL: "Вручную",
    PROMOTION: "Акция",
    GIFT: "Подарок",
    ORDER: "Заказ",
  };
  return translations[sourceType] ?? sourceType;
}

function translatePaymentStatus(status: string): string {
  const translations: Record<string, string> = {
    PENDING: "Ожидание",
    CONFIRMED: "Подтверждён",
    REJECTED: "Отклонён",
    REFUNDED: "Возврат",
    PARTIALLY_REFUNDED: "Частичный возврат",
  };
  return translations[status] ?? status;
}
